function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import * as Sentry from '@sentry/react';

// eslint-disable-next-line @typescript-eslint/no-explicit-any

export class RealIntercomClient {
  constructor() {
    _defineProperty(this, "loadFailedCallbacks", []);
    _defineProperty(this, "loaded", false);
    _defineProperty(this, "cspViolations", []);
    _defineProperty(this, "failedToLoadIntercom", false);
    document.addEventListener('securitypolicyviolation', e => {
      var _e$blockedURI;
      if ((_e$blockedURI = e.blockedURI) !== null && _e$blockedURI !== void 0 && _e$blockedURI.includes('intercom')) {
        this.cspViolations.push("".concat(e.violatedDirective, ": ").concat(e.blockedURI));
      }
    });
  }
  boot(data) {
    this.getIntercom().then(intercom => intercom('boot', data));
  }
  shutdown() {
    this.getIntercom().then(intercom => intercom('shutdown'));
  }
  showNewMessage() {
    if (this.failedToLoadIntercom) {
      window.open('https://intercom.help/anchor-billing/en/', '_blank');
      return;
    }
    this.getIntercom().then(intercom => intercom('showNewMessage'));
  }
  update(data) {
    this.getIntercom().then(intercom => intercom('update', data));
  }
  hideIntercom() {
    this.getIntercom().then(intercom => intercom('update', {
      z_index: 1100
    }));
  }
  showIntercom() {
    this.getIntercom().then(intercom => intercom('update', {
      z_index: 2147483000
    }));
  }
  onLoadFailed(callback) {
    this.loadFailedCallbacks.push(callback);
  }
  isLoaded() {
    return this.loaded;
  }
  notifyLoadFailed(reason) {
    this.loadFailedCallbacks.forEach(cb => cb(reason));
  }
  async safeFetch(url) {
    let timeoutMs = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 3000;
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      await fetch(url, {
        method: 'HEAD',
        mode: 'no-cors',
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      return 'success';
    } catch (error) {
      if (error instanceof Error) {
        return error.name === 'AbortError' ? 'timeout' : "failed: ".concat(error.message);
      }
      return 'failed: unknown';
    }
  }
  async collectDiagnostics(scriptUrl, scriptLoadError) {
    const defaultDiagnostics = {
      likelyCause: 'unknown',
      intercomFetchResult: 'unknown',
      thirdPartyFetchResult: 'unknown',
      cspViolations: [],
      isOnline: navigator.onLine,
      scriptLoadError
    };
    try {
      const [intercomFetchResult, thirdPartyFetchResult] = await Promise.all([this.safeFetch(scriptUrl), this.safeFetch('https://www.google-analytics.com/analytics.js')]);
      const cspViolations = this.cspViolations;
      const intercomBlocked = intercomFetchResult.startsWith('failed');
      const thirdPartyBlocked = thirdPartyFetchResult.startsWith('failed');
      let likelyCause;
      if (!navigator.onLine) {
        likelyCause = 'offline';
      } else if (cspViolations.length > 0) {
        likelyCause = 'csp_violation';
      } else if (intercomBlocked && thirdPartyBlocked) {
        likelyCause = 'ad_blocker_or_privacy_extension';
      } else if (intercomBlocked && !thirdPartyBlocked) {
        likelyCause = 'intercom_specifically_blocked';
      } else if (intercomFetchResult === 'success') {
        likelyCause = 'script_execution_blocked';
      } else if (intercomFetchResult === 'timeout') {
        likelyCause = 'network_timeout';
      } else {
        likelyCause = 'unknown';
      }
      return {
        likelyCause,
        intercomFetchResult,
        thirdPartyFetchResult,
        cspViolations,
        isOnline: navigator.onLine,
        scriptLoadError
      };
    } catch (diagnosticError) {
      Sentry.captureException(diagnosticError, {
        tags: {
          context: 'intercom_diagnostics_collection_failed'
        }
      });
      return defaultDiagnostics;
    }
  }
  reportLoadFailure(diagnostics) {
    try {
      this.notifyLoadFailed(diagnostics.likelyCause);
      Sentry.withScope(scope => {
        scope.setContext('intercom_diagnostics', diagnostics);
        scope.setTag('intercom_likely_cause', diagnostics.likelyCause);
        scope.setTag('intercom_fetch_result', diagnostics.intercomFetchResult);
        scope.setTag('intercom_third_party_blocked', diagnostics.thirdPartyFetchResult.startsWith('failed') ? 'true' : 'false');
        scope.setTag('intercom_has_csp_violations', diagnostics.cspViolations.length > 0 ? 'true' : 'false');
        scope.setLevel('warning');
        Sentry.captureException(new Error('failed to load intercom', {
          cause: diagnostics.scriptLoadError
        }));
      });
    } catch (reportError) {
      Sentry.captureException(reportError, {
        tags: {
          context: 'intercom_report_failure_failed'
        }
      });
    }
  }
  async getIntercom() {
    if (window.enableIntercom && window.isProduction) {
      return window.Intercom || this.loadIntercom();
    }
    return () => {};
  }
  loadIntercom() {
    const APP_ID = window._intercom_app_id = 'yx1cchhq';
    return new Promise(resolve => {
      const w = window;
      const ic = w.Intercom;
      if (typeof ic === 'function') {
        ic('reattach_activator');
        ic('update', w.intercomSettings);
        this.loaded = true;
      } else {
        const d = document;
        const i = function i() {
          // eslint-disable-next-line prefer-rest-params
          i.c(arguments);
        };
        i.q = [];
        i.c = function (args) {
          i.q.push(args);
        };
        w.Intercom = i;
        const l = () => {
          var _x$parentNode;
          const s = d.createElement('script');
          s.type = 'text/javascript';
          s.async = true;
          s.src = 'https://widget.intercom.io/widget/' + APP_ID;
          const x = d.getElementsByTagName('script')[0];
          (_x$parentNode = x.parentNode) === null || _x$parentNode === void 0 || _x$parentNode.insertBefore(s, x);
          s.onload = () => {
            this.loaded = true;
            resolve(window.Intercom);
          };
          s.onerror = error => {
            this.failedToLoadIntercom = true;
            // Extract serializable properties from the error event
            const errorEvent = error;
            const scriptLoadError = {
              message: errorEvent.message || String(error),
              type: errorEvent.type || 'error',
              filename: errorEvent.filename,
              lineno: errorEvent.lineno,
              colno: errorEvent.colno,
              raw: errorEvent
            };
            this.collectDiagnostics(s.src, scriptLoadError).then(diagnostics => this.reportLoadFailure(diagnostics));
          };
        };
        if (document.readyState === 'complete') {
          l();
        } else if (w.attachEvent) {
          w.attachEvent('onload', l);
        } else {
          w.addEventListener('load', l, false);
        }
      }
    });
  }
}